import { CircleInfoIcon } from "@xola/icons";
import { FormGroup, getSymbol, Select, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { sortBy } from "lodash";
import React, { useState } from "react";
import { AddOn, CatalogItem, CatalogItemObject, CatalogPriceType, LineItem, ProductCatalogItem, Seller } from "types";
import { getCatalogItemPhoto } from "../../../models/Catalog";
import { AddonLineItem, CatalogLineItem } from "../../../stores/createPurchaseStore";
import { CatalogItemImage } from "../../Common/CatalogItemImage";
import { NoTranslate } from "../../Common/NoTranslate";
import { AddonNameField, getAddonRowPadding } from "./QuantityAddons";
import { isQuestionnaireAddOn } from "@/models/CatalogItem";
import { getSeller } from "@/utils/getSeller";

interface ChoicesAddonProps {
    catalogItem: AddOn;
    lineItems: LineItem[] | CatalogLineItem[] | AddonLineItem[];
    onChange: (templateIds: ID[], selectedItemId: ID) => void;
    shouldShowDefaultImg?: boolean;
    shouldDecreaseOnly?: boolean;
}

interface NoneOption {
    id: string;
    template: {
        id: string;
        code: string;
    };
    name: string;
}

type Choice = Omit<AddOn, "sequence">;

export const ChoicesAddon = ({
    catalogItem,
    lineItems,
    onChange,
    shouldShowDefaultImg = true,
    shouldDecreaseOnly = false,
}: ChoicesAddonProps) => {
    // Choices inside addon will always have template.id
    const templateIds = catalogItem?.items?.map((item: CatalogItem) => item.template!.id) ?? [];

    const getSelectedValue = () => {
        return (lineItems as CatalogLineItem[]).find(
            (item) => item.template && templateIds.includes(item.template.id) && item.quantity,
        );
    };

    const [selectedValue, setSelectedValue] = useState(getSelectedValue());

    const handleChange = (templateId: string) => {
        const selectedItem = (lineItems as CatalogLineItem[]).find(
            (item) => item.template && item.template.id === templateId,
        );
        onChange(templateIds, templateId);
        setSelectedValue(selectedItem);
    };

    return (
        <ChoicesAddonItem
            catalogItem={catalogItem}
            selectedValue={selectedValue?.template.id}
            shouldDecreaseOnly={shouldDecreaseOnly}
            shouldShowDefaultImg={shouldShowDefaultImg}
            onChange={handleChange}
        />
    );
};

const getChoice = (id: ID, catalogItem: CatalogItem, seller: Seller) => {
    const catalogChoice = catalogItem.items?.find((choice) => choice.id === id);
    const price = catalogChoice?.prices?.price;

    if (!catalogChoice || !price?.min) {
        return catalogChoice?.name;
    }

    return price.priceType === CatalogPriceType.PERCENT
        ? `${price.min}% of purchasable items - ${catalogChoice.name}`
        : `${getSymbol(seller.currency)}${price.min} - ${catalogChoice.name}`;
};

interface ChoicesSelectProps {
    catalogItem: ProductCatalogItem;
    selectedValue?: string;
    shouldDecreaseOnly: boolean;
    shouldShowDefaultImg: boolean;
    onChange: (templateId: string, templateCode: string) => void;
    error?: string;
}

export const ChoicesAddonItem = ({
    catalogItem,
    selectedValue: initialValue,
    shouldDecreaseOnly,
    shouldShowDefaultImg,
    onChange,
    error,
}: ChoicesSelectProps) => {
    const seller = getSeller();
    const [selectedValue, setSelectedValue] = useState(initialValue);
    const isFormAddon = isQuestionnaireAddOn(catalogItem);
    const photoSource = getCatalogItemPhoto(catalogItem);
    const isDefaultProductImgVisible = Boolean(photoSource) || Boolean(shouldShowDefaultImg);
    const rowPadding = getAddonRowPadding(isDefaultProductImgVisible, isFormAddon);

    const catalogItemItems =
        (catalogItem.object === CatalogItemObject.PRODUCT_CATALOG_ITEM
            ? sortBy(catalogItem?.items, "sequence")
            : catalogItem?.items) ?? [];

    if (catalogItemItems.every((item) => item.deletedAt)) {
        return null;
    }

    const choices: Array<Choice | NoneOption> = [
        {
            id: "none",
            template: { id: "None", code: "None" },
            name: selectedValue ? "Remove" : "Select",
        },
        ...catalogItemItems,
    ];

    const handleChange = (value: string) => {
        const choice = choices.find((choice) => choice.template?.id === value);
        const template = choice?.template;
        if (!template?.code || !template?.id) {
            return;
        }
        onChange(template.id, template.code);
        setSelectedValue(template.id);
    };

    return (
        <div data-testid="choices-addon-item" className={clsx("flex w-full flex-wrap items-start", rowPadding)}>
            {isDefaultProductImgVisible ? (
                <CatalogItemImage catalogItem={catalogItem} size={isFormAddon ? "small" : "medium"} />
            ) : null}

            <div className="flex w-full flex-1 flex-col justify-center">
                <div className="flex gap-1">
                    <AddonNameField isFormAddon={isFormAddon} error={error} catalogItem={catalogItem} />
                    {catalogItem?.caption && (
                        <Tooltip content={catalogItem.caption}>
                            <CircleInfoIcon />
                        </Tooltip>
                    )}
                    {!selectedValue && (
                        <div className={clsx("text-gray-darker", isFormAddon ? "text-sm" : "text-base")}>
                            Price Varies
                        </div>
                    )}
                </div>
                <FormGroup className="mb-0 pt-2">
                    <NoTranslate>
                        <Select
                            className={clsx("!w-full", "cursor-pointer overflow-hidden text-ellipsis pr-7 pt-2")}
                            data-testid="choices-dropdown-button"
                            value={selectedValue}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange(e.target.value)}
                        >
                            {choices.map((choice: Choice | NoneOption) => {
                                const { template, id } = choice;

                                if ("deletedAt" in choice && choice.deletedAt) {
                                    return null;
                                }

                                if (template?.id === "None") {
                                    return (
                                        <option key={template.id} className="italic" value={template.id}>
                                            {selectedValue ? "Remove" : "Select"}
                                        </option>
                                    );
                                }

                                if (selectedValue === template?.id || !shouldDecreaseOnly) {
                                    return (
                                        <option
                                            key={`${template?.id}-${id}`}
                                            className="no-translate !w-20"
                                            value={template?.id}
                                        >
                                            {getChoice(id, catalogItem, seller)}
                                        </option>
                                    );
                                }

                                return null;
                            })}
                        </Select>
                    </NoTranslate>
                </FormGroup>
            </div>
        </div>
    );
};
